import { Ref } from "react";
import { TaskType } from "../../../types/TaskType";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import {
  isLoading,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";
import CreatorFragmentConfig from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragmentConfig";
import { shouldShowField } from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import CustomTaskDescriptionCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CustomTaskDescriptionCreatorFragment/CustomTaskDescriptionCreatorFragment";
import SwitchCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/SwitchCreatorFragment/SwitchCreatorFragment";
import TextModalCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/TextModalCreatorFragment/TextModalCreatorFragment";
import AssignToCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/AssignToCreatorFragment/AssignToCreatorFragment";
import InvalidCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/InvalidCreatorFragment/InvalidCreatorFragment";
import SelectListItemCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/SelectListItemCreatorFragment/SelectListItemCreatorFragment";
import AttachArticlesCreatorFragment from "./AttachArticlesCreatorFragment/AttachArticlesCreatorFragment";
import AddLocationCreatorFragment from "./AddLocationCreatorFragment/AddLocationCreatorFragment";
import AttachArticleCreatorFragment from "./AttachArticleCreatorFragment/AttachArticleCreatorFragment";
import ChangeArticleCreatorFragment from "./ChangeArticleCreatorFragment/ChangeArticleCreatorFragment";
import SelectListItemWithDetailsCreatorFragment from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/SelectListItemWithDetailsCreatorFragment/SelectListItemWithDetailsCreatorFragment";
import TitledHeadingTextCreatorFragment from "./TitledHeadingTextCreatorFragment/TitledHeadingTextCreatorFragment";
import TaskInfoPanelCreatorFragment from "./TaskInfoPanelCreatorFragment/TaskInfoPanelCreatorFragment";
import TitledTextAreaCreatorFragment from "./TitledTextAreaCreatorFragment/TitledTextAreaCreatorFragment";
import ArticlesCreatorFragment from "./ArticlesCreatorFragment/ArticlesCreatorFragment";
import MediaLocationCreatorFragment from "./MediaLocationCreatorFragment/MediaLocationCreatorFragment";
import SegmentedControlCreatorFragment from "./SegmentedControlCreatorFragment/SegmentedControlCreatorFragment";
import PickupAndRefillLocationCreatorFragment from "./PickupAndRefillLocationCreatorFragment/PickupAndRefillLocationCreatorFragment";
import ArticlesWithQuantityCreatorFragment from "./ArticlesQuantityCreatorFragment/ArticlesQuantityCreatorFragment";
import ArticlesPrintPtagsCreatorFragment from "./ArticlesPrintPtagsCreatorFragment/ArticlesPrintPtagsCreatorFragment";
import RefillQuantityCreatorFragment from "./RefillQuantityCreatorFragment/RefillQuantityCreatorFragment";
import MediasLocationCreatorFragment from "./MediasLocationCreatorFragment/MediasLocationCreatorFragment";
import MediasQuantityCreatorFragment from "./MediasQuantityCreatorFragment/MediasQuantityCreatorFragment";
import AttachArticlesWithQuantityStepperCreatorFragment from "./AttachArticlesWithQuantityStepperCreatorFragment/AttachArticlesWithQuantityStepperCreatorFragment";
import MarkAsCompleteCreatorFragment from "./MarkAsCompleteCreatorFragment/MarkAsCompleteCreatorFragment";
import FailedCriteriaCreatorFragment from "./FailedCriteriaCreatorFragment/FailedCriteriaCreatorFragment";
import FailedCriteriaSummaryCreatorFragment from "./FailedCriteriaSummaryCreatorFragment/FailedCriteriaSummaryCreatorFragment";
import LocatedItemCreatorFragment from "./LocatedItemCreatorFragment/LocatedItemCreatorFragment";
import MediasPrintPtagsCreatorFragment from "./MediasPrintPtagsCreatorFragment/MediasPrintPtagsCreatorFragment";
import SelectMultipleListItemCreatorFragment from "./SelectMultipleListItemCreatorFragment/SelectMultipleListItemCreatorFragment";
import SelectNumberCreatorFragment from "./SelectNumberCreatorFragment/SelectNumberCreatorFragment";
import SupplierNumberCreatorFragment from "./SupplierNumberCreatorFragment/SupplierNumberCreatorFragment";
import DateStampCreatorFragment from "./DateStampCreatorFragment/DateStampCreatorFragment";
import SafetyAlarmCreatorFragment from "./SafetyAlarmCreatorFragment/SafetyAlarmCreatorFragment";
import NumberInputCreatorFragment from "./NumberInputCreatorFragment/NumberInputCreatorFragment";

export type CreatorFragmentHandle = {
  valid: boolean;
};

export type CreatorFragmentCommonProps = {
  // Block all user interaction with this fragment, usually while making api requests.
  blockInput: boolean;
  // The task being created or edited.
  task: Record<string, unknown> | Loading;
  // Callback for making changes to the task that is being created or edited.
  // Note that this doesn't trigger any api request. It just adds a part to the PatchTask
  // that is sent when the user explicitly creates or saves the task later on.
  onTaskChange: (part: Record<string, unknown>) => void;
  // Whether to show error validation at the top level.
  // This is toggled if the user tries to create or save a task containing invalid data.
  showValidation: boolean;
  taskType: TaskType | Loading;
};

type CreatorFragmentProps = {
  config: CreatorFragmentConfig;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function CreatorFragment({ config, commonProps, ref }: CreatorFragmentProps) {
  const { id, props: configProps } = config;

  switch (id) {
    case "custom-task-description-creator-fragment": {
      return (
        <CustomTaskDescriptionCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "text-modal-creator-fragment": {
      return (
        <TextModalCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "switch-creator-fragment": {
      return (
        <SwitchCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "assign-to-creator-fragment": {
      return (
        <AssignToCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "attach-articles-creator-fragment": {
      return (
        <AttachArticlesCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "add-location-creator-fragment": {
      return (
        <AddLocationCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "select-list-item-with-description-creator-fragment": {
      return (
        <SelectListItemWithDetailsCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "select-list-item-creator-fragment": {
      return (
        <SelectListItemCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "select-multiple-list-item-creator-fragment": {
      return (
        <SelectMultipleListItemCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "select-number-creator-fragment": {
      return (
        <SelectNumberCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "supplier-number-creator-fragment": {
      return (
        <SupplierNumberCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "date-stamp-creator-fragment": {
      return (
        <DateStampCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "safety-alarm-creator-fragment": {
      return (
        <SafetyAlarmCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "number-input-creator-fragment": {
      return (
        <NumberInputCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "attach-article-creator-fragment": {
      return (
        <AttachArticleCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "change-article-creator-fragment": {
      return (
        <ChangeArticleCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "titled-heading-text-creator-fragment": {
      return (
        <TitledHeadingTextCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "articles-creator-fragment": {
      return (
        <ArticlesCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "task-info-panel-creator-fragment": {
      return (
        <TaskInfoPanelCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "titled-text-area-creator-fragment": {
      return (
        <TitledTextAreaCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "segmented-control-creator-fragment": {
      return (
        <SegmentedControlCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "articles-quantity-creator-fragment": {
      return (
        <ArticlesWithQuantityCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "pickup-and-refill-location-creator-fragment": {
      return (
        <PickupAndRefillLocationCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "articles-print-ptags-creator-fragment": {
      return (
        <ArticlesPrintPtagsCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "refill-quantity-creator-fragment": {
      return (
        <RefillQuantityCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "media-location-creator-fragment": {
      return (
        <MediaLocationCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "medias-location-creator-fragment": {
      return (
        <MediasLocationCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "medias-quantity-creator-fragment": {
      return (
        <MediasQuantityCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "medias-print-ptags-creator-fragment": {
      return (
        <MediasPrintPtagsCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "attach-articles-with-quantity-stepper-creator-fragment": {
      return (
        <AttachArticlesWithQuantityStepperCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "mark-as-complete-creator-fragment": {
      return (
        <MarkAsCompleteCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
        />
      );
    }
    case "failed-criteria-creator-fragment": {
      return (
        <FailedCriteriaCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "located-item-creator-fragment": {
      return (
        <LocatedItemCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    case "failed-criteria-summary-creator-fragment": {
      return (
        <FailedCriteriaSummaryCreatorFragment
          configProps={configProps}
          commonProps={commonProps}
          ref={ref}
        />
      );
    }
    default: {
      console.error(`'${id}' does not map to any creator fragment component`);
      reportMessageToSentry(
        `Creator fragment config id does not map to any creator fragment component`,
        {
          id,
        },
        "error"
      );
      return <InvalidCreatorFragment />;
    }
  }
}

export default CreatorFragment;
